/**
 * Copy for saved issues on the dashboard Issues page (2026-10-25), the third
 * surface after the extension sidebar (useProjectIssuesBulkOps) and the team
 * review drawer (useIssueSelection.copyIssues), which shipped it in ccc143b29.
 * Same shared mapper, formatter, labels and toast copy as both, so the three
 * lists cannot copy the same issue three ways.
 *
 * Writes nothing to the server, so a read-only viewer may use it. Issues keep
 * the order the table shows them: `issues` is the DataTable's `data`, rendered
 * in array order, so filtering it by the selection IS the table order.
 */
import { toast } from 'sonner';
import {
  COPY_FAILED_MESSAGE,
  ISSUES_COPY_HEADING,
  formatFindingsForClipboard,
  issuesCopiedMessage,
  savedIssueToClipboard,
  writeClipboardTextDeferred,
  type ClipboardFormat,
  type ClipboardSavedIssue,
  type Ticket,
} from '@overlayqa/shared';
import { copyText } from '../../lib/copyText';
import { capture } from '../../lib/analytics';
import { issuesApi } from '../../lib/api/issues';
import { log } from '../../lib/logger';

export type CopyIssuesMenu = 'row' | 'bulk';

/**
 * The API row carries browser and viewport FLAT (`browserName`, `browserVersion`,
 * `os`, `viewportWidth`, `viewportHeight`: the issues table's own columns, the
 * shape team review's Issue type reads), while the shared Ticket type declares
 * them nested under `browserInfo` and `viewport`, which the list never sends.
 * Read the flat fields first and fall back to the nested ones, so a row from
 * either shape copies its Browser, OS and Viewport rows. (The first cut read
 * only the nested shape and so wrote `undefined` over the flat values: the
 * copied Markdown lost those three rows on every real issue.)
 */
type FlatCaptureFields = Partial<Pick<ClipboardSavedIssue, 'browserName' | 'browserVersion' | 'os' | 'viewportWidth' | 'viewportHeight'>>;

export function ticketToClipboard(ticket: Ticket) {
  const flat = ticket as Ticket & FlatCaptureFields;
  return savedIssueToClipboard({
    ...ticket,
    browserName: flat.browserName ?? ticket.browserInfo?.name,
    browserVersion: flat.browserVersion ?? ticket.browserInfo?.version,
    os: flat.os ?? ticket.browserInfo?.os,
    viewportWidth: flat.viewportWidth ?? ticket.viewport?.width,
    viewportHeight: flat.viewportHeight ?? ticket.viewport?.height,
  });
}

/**
 * Copy `ids` out of `issues` (the list as displayed) in `format`. Resolves to
 * the number copied: 0 when nothing matched or the clipboard refused the write,
 * in which case the failure toast has already been shown and nothing is tracked.
 */
export async function copyIssues(
  issues: readonly Ticket[],
  ids: readonly string[],
  format: ClipboardFormat,
  menu: CopyIssuesMenu,
  getToken?: () => Promise<string | null>,
  signedIn = false,
): Promise<number> {
  const wanted = new Set(ids);
  const list = issues.filter((issue) => wanted.has(issue.id));
  if (list.length === 0) return 0;
  const local = () => formatFindingsForClipboard(list.map(ticketToClipboard), format, { heading: ISSUES_COPY_HEADING });
  // Text and Markdown come from the export formatter (POST /api/issues/copy,
  // 2026-10-28), so a paste carries CSS, element details, Figma, AI analysis,
  // discussion and the issue link, plus its HTML (2026-10-28, "proceed") so a
  // rich editor pastes the formatted issue with its screenshot. A failed render
  // completes the same item with local prose and escaped HTML; no rendered images.
  // CSV keeps its columns, text only.
  // The write starts inside the click.
  const rendered = format === 'csv' || !getToken ? null : (async () => {
    try {
      const out = await issuesApi.copyText(await getToken(), list.map((issue) => issue.id), format === 'markdown' ? 'markdown' : 'plain-text');
      if (out.text) return out;
    } catch (err) {
      // error, not warn: warn prints only in verbose mode, and a silent fallback
      // would hide a broken route behind copies that still "work".
      log.error('[copyIssues] Render failed; copying the local format', { count: list.length, format, error: err instanceof Error ? err.message : String(err) });
    }
    return null;
  })();
  const text = rendered ? rendered.then((out) => out?.text ?? local()) : Promise.resolve(local());
  if (!(await writeClipboardTextDeferred(text, copyText, rendered?.then((out) => out?.html || null)))) {
    toast.error(COPY_FAILED_MESSAGE);
    return 0;
  }
  toast.success(issuesCopiedMessage(list.length));
  const withScreenshot = list.filter((issue) => issue.screenshotUrl?.trim()).length;
  capture('issues_copied', { count: list.length, format, menu, surface: 'dashboard', signed_in: signedIn, has_screenshot: withScreenshot > 0, with_screenshot: withScreenshot });
  return list.length;
}
